import {
  ContractVersion,
  ContractVersionHeader,
  type ErrorResponse,
} from "@carbon-operations/contracts";

export type MutationResult = {
  eventId?: string;
  requestId?: string;
  runId?: string;
  status: "acknowledged";
};

export type MutationBoundary = {
  csrfToken: string;
  idempotencyKey: string;
  tenantId: string;
  signal?: AbortSignal;
};

export type ReadBoundary = { tenantId?: string; signal?: AbortSignal };
export const SessionEndedEvent = "carbon-operations:session-ended";

export function createIdempotencyKey(): string {
  return crypto.randomUUID();
}

export async function readApi<T>(
  path: string,
  boundary: ReadBoundary = {},
): Promise<T> {
  const response = await fetch(`/api/carbon-operations/v1${path}`, {
    credentials: "include",
    ...(boundary.signal ? { signal: boundary.signal } : {}),
    headers: {
      Accept: "application/json",
      [ContractVersionHeader]: ContractVersion,
      ...(boundary.tenantId ? { "X-Carbon-Tenant-Id": boundary.tenantId } : {}),
    },
  });
  signalEndedSession(response);
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as
      | ErrorResponse
      | null;
    throw new Error(
      payload?.message ?? `Control plane request failed (${response.status})`,
    );
  }
  return response.json() as Promise<T>;
}

export async function readApiBytes(
  path: string,
  boundary: ReadBoundary,
): Promise<{ bytes: ArrayBuffer; mediaType: string }> {
  if (
    !/^\/api\/carbon-operations\/v1\/artifacts\/[A-Za-z0-9][A-Za-z0-9_-]*\/content$/
      .test(
        path,
      )
  ) throw new Error("Artifact content path is outside the same-origin v1 API");
  const response = await fetch(path, {
    credentials: "include",
    headers: {
      Accept: "application/octet-stream",
      [ContractVersionHeader]: ContractVersion,
      ...(boundary.tenantId ? { "X-Carbon-Tenant-Id": boundary.tenantId } : {}),
    },
  });
  signalEndedSession(response);
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as
      | ErrorResponse
      | null;
    throw new Error(
      payload?.message ??
        `Artifact content request failed (${response.status})`,
    );
  }
  return {
    bytes: await response.arrayBuffer(),
    mediaType: response.headers.get("content-type")?.split(";")[0]?.trim() ||
      "application/octet-stream",
  };
}

export async function acknowledgedMutation(
  path: string,
  body: unknown | undefined,
  boundary: MutationBoundary,
): Promise<MutationResult> {
  const response = await mutationResponse(path, body, boundary);
  const payload = await response.json().catch(() => ({})) as {
    event_id?: string;
    request_id?: string;
    run_id?: string;
  };
  return {
    status: "acknowledged",
    ...(payload.event_id ? { eventId: payload.event_id } : {}),
    ...(payload.request_id ? { requestId: payload.request_id } : {}),
    ...(payload.run_id ? { runId: payload.run_id } : {}),
  };
}

export async function mutationApi<T>(
  path: string,
  body: unknown | undefined,
  boundary: MutationBoundary,
): Promise<T> {
  const response = await mutationResponse(path, body, boundary);
  return response.json() as Promise<T>;
}

async function mutationResponse(
  path: string,
  body: unknown | undefined,
  boundary: MutationBoundary,
): Promise<Response> {
  const response = await fetch(`/api/carbon-operations/v1${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      ...(body === undefined ? {} : { "content-type": "application/json" }),
      [ContractVersionHeader]: ContractVersion,
      "X-Carbon-Tenant-Id": boundary.tenantId,
      "X-CSRF-Token": boundary.csrfToken,
      "Idempotency-Key": boundary.idempotencyKey,
    },
    credentials: "include",
    ...(boundary.signal ? { signal: boundary.signal } : {}),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  signalEndedSession(response);
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as
      | ErrorResponse
      | null;
    throw new Error(
      payload?.message ??
        `Control plane did not acknowledge the change (${response.status})`,
    );
  }
  return response;
}

function signalEndedSession(response: Response): void {
  if (
    response.status === 401 &&
    typeof globalThis.dispatchEvent === "function"
  ) globalThis.dispatchEvent(new Event(SessionEndedEvent));
}
